import React from "react";
import { BRAND, LOGOS } from "../../lib/defaultFlyer";

/**
 * HTML preview of the flyer. Visually mirrors the @react-pdf/renderer
 * FlyerDocument output, but renders as ordinary DOM for live preview.
 *
 * The PDF download is generated separately from FlyerDocument so the two
 * stay in lock-step on content; styles are deliberately matched by hand.
 *
 * Outer container fits a 794 x 1123 (A4 @ 96 dpi) canvas inside the
 * available preview area using CSS transform: scale.
 */
const A4_W = 794;
const A4_H = 1123;

const resolveLogo = (flyer) => {
  if (flyer.customLogo) return flyer.customLogo;
  const found = LOGOS.find((l) => l.id === flyer.logoId);
  return (found || LOGOS[0]).src;
};

const Paw = ({ color = BRAND.sage, size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24">
    <circle cx="6" cy="8" r="2.2" fill={color} />
    <circle cx="12" cy="5.5" r="2.2" fill={color} />
    <circle cx="18" cy="8" r="2.2" fill={color} />
    <circle cx="8" cy="13.5" r="2.2" fill={color} />
    <circle cx="16" cy="13.5" r="2.2" fill={color} />
    <path
      d="M7 19 C7 15.5 9 14 12 14 C15 14 17 15.5 17 19 C17 21 15.5 22 12 22 C8.5 22 7 21 7 19 Z"
      fill={color}
    />
  </svg>
);

const FlyerHtmlPreview = ({ flyer, scale = 1 }) => {
  const logoSrc = resolveLogo(flyer);

  return (
    <div
      data-testid="flyer-html-page"
      style={{
        width: A4_W,
        height: A4_H,
        transform: `scale(${scale})`,
        transformOrigin: "top left",
        background: BRAND.paper,
        color: BRAND.ink,
        fontFamily: "Carlito, system-ui, sans-serif",
        fontSize: 13,
        lineHeight: 1.4,
        padding: "48px 50px",
        boxSizing: "border-box",
        boxShadow: "0 10px 40px rgba(17, 66, 50, 0.12)",
        borderRadius: 4,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* HEADER */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 18,
        }}
      >
        <img src={logoSrc} alt="" style={{ width: 92, height: 92, objectFit: "contain" }} />
        <div style={{ textAlign: "right" }}>
          <div
            style={{
              fontFamily: "Chewy, system-ui",
              fontSize: 28,
              color: BRAND.forest,
              letterSpacing: 1.5,
              lineHeight: 1,
            }}
          >
            {flyer.brand}
          </div>
          <div
            style={{
              fontFamily: "Chewy, system-ui",
              fontSize: 14,
              color: BRAND.sage,
              marginTop: 4,
              letterSpacing: 1,
            }}
          >
            {flyer.tagline}
          </div>
        </div>
      </div>

      {/* HERO */}
      <div
        style={{
          background: BRAND.forest,
          borderRadius: 18,
          padding: 28,
          color: BRAND.cream,
          display: "flex",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {[{ s: 90, x: -25, y: -25, o: 0.12 }, { s: 50, x: 420, y: 160, o: 0.16 }, { s: 32, x: 330, y: -12, o: 0.2 }].map((b, i) => (
          <div
            key={i}
            style={{
              position: "absolute",
              left: b.x,
              top: b.y,
              width: b.s,
              height: b.s,
              borderRadius: b.s / 2,
              background: BRAND.sage,
              opacity: b.o,
            }}
          />
        ))}
        <div style={{ flex: 1, paddingRight: 16, position: "relative", zIndex: 1 }}>
          <div
            style={{
              fontSize: 11,
              color: BRAND.sage,
              letterSpacing: 1.8,
              textTransform: "uppercase",
              fontWeight: 700,
              marginBottom: 14,
            }}
          >
            {flyer.eyebrow}
          </div>
          <div
            style={{
              fontFamily: "Chewy, system-ui",
              fontSize: 34,
              lineHeight: 1.1,
              marginBottom: 14,
            }}
          >
            {flyer.headline}
          </div>
          <div style={{ fontSize: 13.5, color: "#D6DED7", lineHeight: 1.55, maxWidth: 460 }}>
            {flyer.subheadline}
          </div>
        </div>
        <div style={{ width: 140, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1 }}>
          <img src={logoSrc} alt="" style={{ width: 124, height: 124, objectFit: "contain" }} />
        </div>
      </div>

      {/* BENEFITS */}
      <div style={{ display: "flex", gap: 12, marginTop: 18 }}>
        {flyer.benefits.map((b, i) => (
          <div
            key={i}
            style={{
              flex: 1,
              background: BRAND.cream,
              borderRadius: 12,
              padding: 16,
              borderLeft: `4px solid ${BRAND.forest}`,
            }}
          >
            <div style={{ fontFamily: "Chewy, system-ui", fontSize: 16, color: BRAND.forest, marginBottom: 6 }}>
              {b.title}
            </div>
            <div style={{ fontSize: 11.5, color: "#3A4F47", lineHeight: 1.5 }}>{b.body}</div>
          </div>
        ))}
      </div>

      {/* STATS */}
      <div
        style={{
          display: "flex",
          marginTop: 18,
          background: BRAND.bone,
          borderRadius: 12,
          padding: 16,
        }}
      >
        {flyer.stats.map((st, i) => (
          <div
            key={i}
            style={{
              flex: 1,
              textAlign: "center",
              borderRight: i === flyer.stats.length - 1 ? "none" : `1px solid ${BRAND.sage}`,
              padding: "0 6px",
            }}
          >
            <div style={{ fontFamily: "Chewy, system-ui", fontSize: 26, color: BRAND.forest }}>
              {st.value}
            </div>
            <div
              style={{
                fontSize: 10,
                color: "#3A4F47",
                textTransform: "uppercase",
                letterSpacing: 0.8,
                marginTop: 3,
              }}
            >
              {st.label}
            </div>
          </div>
        ))}
      </div>

      {/* PROCESS */}
      <div
        style={{
          fontFamily: "Chewy, system-ui",
          fontSize: 19,
          color: BRAND.forest,
          marginTop: 22,
          marginBottom: 10,
        }}
      >
        How it works
      </div>
      <div style={{ display: "flex", gap: 12 }}>
        {flyer.process.map((p, i) => (
          <div
            key={i}
            style={{
              flex: 1,
              padding: 14,
              borderRadius: 12,
              border: `1px solid ${BRAND.sage}`,
              background: BRAND.paper,
            }}
          >
            <div style={{ fontFamily: "Chewy, system-ui", color: BRAND.sage, fontSize: 24, marginBottom: 2 }}>
              {p.step}
            </div>
            <div style={{ fontFamily: "Chewy, system-ui", fontSize: 15, color: BRAND.forest, marginBottom: 4 }}>
              {p.title}
            </div>
            <div style={{ fontSize: 11.5, color: "#3A4F47", lineHeight: 1.5 }}>{p.body}</div>
          </div>
        ))}
      </div>

      {/* REQUIREMENTS */}
      <div
        style={{
          fontFamily: "Chewy, system-ui",
          fontSize: 19,
          color: BRAND.forest,
          marginTop: 22,
          marginBottom: 10,
        }}
      >
        The deal
      </div>
      <div style={{ display: "flex", gap: 12 }}>
        {[
          { title: "You provide", items: flyer.requirements.youProvide },
          { title: "We provide", items: flyer.requirements.weProvide },
        ].map((col, i) => (
          <div
            key={i}
            style={{
              flex: 1,
              background: BRAND.cream,
              borderRadius: 12,
              padding: 14,
            }}
          >
            <div style={{ fontFamily: "Chewy, system-ui", fontSize: 15, color: BRAND.forest, marginBottom: 8 }}>
              {col.title}
            </div>
            {col.items.map((it, j) => (
              <div
                key={j}
                style={{ display: "flex", alignItems: "flex-start", marginBottom: 4 }}
              >
                <div
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 4,
                    background: BRAND.forest,
                    marginTop: 6,
                    marginRight: 8,
                    flexShrink: 0,
                  }}
                />
                <div style={{ fontSize: 12, color: "#3A4F47" }}>{it}</div>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* CTA */}
      <div
        style={{
          marginTop: 18,
          background: BRAND.forest,
          borderRadius: 14,
          padding: 22,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ flex: 1, paddingRight: 18 }}>
          <div style={{ fontFamily: "Chewy, system-ui", fontSize: 20, color: BRAND.cream, marginBottom: 4 }}>
            Let&apos;s talk
          </div>
          <div style={{ fontSize: 12, color: "#C7D2CA", lineHeight: 1.5 }}>{flyer.cta}</div>
        </div>
        <div style={{ minWidth: 210 }}>
          <div style={{ fontFamily: "Chewy, system-ui", fontSize: 16, color: BRAND.cream, marginBottom: 4 }}>
            {flyer.contact.name}
          </div>
          {flyer.contact.role && (
            <div style={{ fontSize: 11.5, color: BRAND.cream, marginBottom: 2 }}>{flyer.contact.role}</div>
          )}
          {flyer.contact.email && (
            <div style={{ fontSize: 11.5, color: BRAND.cream, marginBottom: 2 }}>{flyer.contact.email}</div>
          )}
          {flyer.contact.phone && (
            <div style={{ fontSize: 11.5, color: BRAND.cream, marginBottom: 2 }}>{flyer.contact.phone}</div>
          )}
          {flyer.contact.website && (
            <div style={{ fontSize: 11.5, color: BRAND.cream, marginBottom: 2 }}>{flyer.contact.website}</div>
          )}
          {flyer.contact.location && (
            <div style={{ fontSize: 11.5, color: BRAND.cream, marginBottom: 2 }}>{flyer.contact.location}</div>
          )}
        </div>
      </div>

      {/* FOOTER */}
      <div
        style={{
          marginTop: 14,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          paddingTop: 10,
          borderTop: `1px solid ${BRAND.sage}`,
        }}
      >
        <div
          style={{
            fontSize: 10,
            color: BRAND.sage,
            letterSpacing: 1.2,
            textTransform: "uppercase",
          }}
        >
          {flyer.footerNote}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <Paw />
          <Paw />
          <Paw />
        </div>
      </div>
    </div>
  );
};

export default FlyerHtmlPreview;
